import { useCallback } from "react";

import { modelServerTimestamp } from "@doitintl/models-any";

import { useApiContext } from "../../../api/context";
import { useUpdateLogin } from "../../../Components/hooks/useOnePassword";
import { consoleErrorWithSentry } from "../../../utils";
import { mpaVaultId } from "../consts";
import useGoogleGroup from "../hooks/useGoogleGroup";
import usePortfolioShare from "../hooks/usePortfolioShare";
import { type MasterPayerAccountsModelSnapshot } from "../hooks/useSubscribedMasterPayerAccounts";
import NewMpaDialog, { type MpaSubmitObject } from "./NewMpaDialog";

export class UpdatePendingMasterPayerAccountError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "UpdatePendingMasterPayerAccountError";
  }
}

export function useUpdatePendingMasterPayerAccount() {
  const updateLogin = useUpdateLogin();
  const { updateGoogleGroup } = useGoogleGroup();
  const { shareAWSPortfolio } = usePortfolioShare();
  const api = useApiContext();

  return useCallback(
    async (masterPayerAccount: MasterPayerAccountsModelSnapshot, mpaActivationData: MpaSubmitObject) => {
      const failureMessages: string[] = [];
      const changes: string[] = [];

      const trackFieldChange = (
        label: string,
        prevValue?: string | boolean | number | null,
        newValue?: string | boolean | number | null,
        formatValue: (value: string | boolean | number | null) => string = (v) => String(v ?? "")
      ) => {
        if (prevValue !== newValue) {
          changes.push(`${label}: ${formatValue(newValue ?? null)}`);
        }
      };

      trackFieldChange("Support model", masterPayerAccount.support.model, mpaActivationData.support.model);
      trackFieldChange("Support tier", masterPayerAccount.support.tier, mpaActivationData.support.tier);

      if (masterPayerAccount.expectedOnboardingDate?.seconds !== mpaActivationData.expectedOnboardingDate?.seconds) {
        const date = mpaActivationData.expectedOnboardingDate
          ? new Date(mpaActivationData.expectedOnboardingDate.seconds * 1000).toLocaleString("en-US", {
              year: "numeric",
              month: "numeric",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            })
          : "none";
        changes.push(`Expected onboarding time: ${date}`);
      }

      trackFieldChange(
        "Apply the above Support tier to all member accounts",
        masterPayerAccount.support.includeMemberAccounts,
        mpaActivationData.support.includeMemberAccounts,
        (v) => String(v ?? false)
      );
      trackFieldChange(
        "Organization import",
        masterPayerAccount.features?.["import-org"],
        mpaActivationData.features?.["import-org"]
      );
      if (!mpaActivationData.features?.["import-org"]) {
        trackFieldChange(
          "No import org reason",
          masterPayerAccount.features?.["no-import-org-reason"],
          mpaActivationData.features?.["no-import-org-reason"],
          (v) => `"${v}"`
        );
      }
      trackFieldChange("AWS Account ID", masterPayerAccount.accountNumber, mpaActivationData.accountNumber);
      trackFieldChange("EDP/PPA", masterPayerAccount.features?.["edp-ppa"], mpaActivationData.features?.["edp-ppa"]);
      trackFieldChange(
        "No root access",
        masterPayerAccount.features?.["no-root-access"],
        mpaActivationData.features?.["no-root-access"]
      );
      if (mpaActivationData.features?.["no-root-access"]) {
        trackFieldChange(
          "Customer has CTA",
          masterPayerAccount.features?.["customer-has-cta"],
          mpaActivationData.features?.["customer-has-cta"]
        );
        trackFieldChange(
          "Expected monthly recurring revenue",
          masterPayerAccount.features?.["nra-expected-mrr"],
          mpaActivationData.features?.["nra-expected-mrr"],
          (v) => `$${v}`
        );
        trackFieldChange(
          "Link to supporting materials",
          masterPayerAccount.features?.["nra-link-to-materials"],
          mpaActivationData.features?.["nra-link-to-materials"],
          (v) => String(v || "none")
        );
      }

      const hasTagsChanged = JSON.stringify(masterPayerAccount.tags) !== JSON.stringify(mpaActivationData.tags);
      if (hasTagsChanged) {
        const allTags = mpaActivationData.tags?.length ? mpaActivationData.tags.join(", ") : "none";
        changes.push(`Tags: ${allTags}`);
      }

      trackFieldChange("Existing customer", masterPayerAccount.existingCustomer, mpaActivationData.existingCustomer);
      if (mpaActivationData.existingCustomer) {
        trackFieldChange(
          "Existing customer service tier",
          masterPayerAccount.existingCustomerTier,
          mpaActivationData.existingCustomerTier
        );
      }

      trackFieldChange(
        "CUR ingestion needed",
        masterPayerAccount.features?.["is-cur-ingestion-needed"],
        mpaActivationData.features?.["is-cur-ingestion-needed"]
      );
      if (mpaActivationData.features?.["is-cur-ingestion-needed"]) {
        trackFieldChange(
          "CUR ingestion enabled per steps",
          masterPayerAccount.features?.["is-cur-ingestion-enabled-per-steps"],
          mpaActivationData.features?.["is-cur-ingestion-enabled-per-steps"],
          (v) => String(v ?? false)
        );
        trackFieldChange(
          "Member Account S3 Bucket Name",
          masterPayerAccount.features?.["member-account-s3-bucket-name"],
          mpaActivationData.features?.["member-account-s3-bucket-name"],
          (v) => String(v || "none")
        );
      }

      const hasServicesChanged =
        JSON.stringify(masterPayerAccount.services) !== JSON.stringify(mpaActivationData.services);
      if (hasServicesChanged) {
        const allServices = mpaActivationData.services?.length
          ? mpaActivationData.services.map((s) => `${s.type}${s.tier ? ` (Service Tier: ${s.tier})` : ""}`).join(", ")
          : "none";
        changes.push(`DoiT Plans: ${allServices}`);
      }

      if (masterPayerAccount.status === "pending") {
        await updatePendingMasterPayerAccount(masterPayerAccount, mpaActivationData);
      } else if (masterPayerAccount.status === "active") {
        await updateActiveMasterPayerAccount(masterPayerAccount, mpaActivationData);
      }

      // if domain has changed
      if (
        masterPayerAccount.domain &&
        mpaActivationData.domain &&
        masterPayerAccount.rootEmail &&
        masterPayerAccount.domain !== mpaActivationData.domain
      ) {
        changes.push(`Domain changed from ${masterPayerAccount.domain} to ${mpaActivationData.domain}`);
        if (mpaActivationData.rootEmail) {
          try {
            await updateGoogleGroup(masterPayerAccount.rootEmail, {
              domain: mpaActivationData.domain,
              rootEmail: mpaActivationData.rootEmail,
            });
          } catch (e) {
            consoleErrorWithSentry(e);
            failureMessages.push("Failed to update Google Group");
          }
        }

        if (masterPayerAccount.onePasswordItemId) {
          const title = `${mpaActivationData.domain} | ${masterPayerAccount.name}`;
          const username = masterPayerAccount.rootEmail.replace(
            masterPayerAccount.rootEmail.split(".")[1].split(/[+@]/)[0],
            mpaActivationData.domain.replaceAll(".", "-")
          );

          try {
            await updateLogin({
              vaultId: mpaVaultId,
              itemId: masterPayerAccount.onePasswordItemId,
              title,
              username,
            });
          } catch (e) {
            consoleErrorWithSentry(e);
            failureMessages.push("Failed to update OnePassword");
          }
        }
      }

      if (mpaActivationData.accountNumber) {
        try {
          await shareAWSPortfolio(mpaActivationData.accountNumber);
        } catch (e) {
          consoleErrorWithSentry(e);
          failureMessages.push("Failed to share AWS Portfolio");
        }
      }

      if (changes.length > 0 && masterPayerAccount.onboardingTicketId) {
        try {
          const comment = `MPA fields updated from console:<br><br>• ${changes.join("<br>• ")}`;
          await api.request({
            method: "put",
            url: `/support/customers/${masterPayerAccount.customerId}/tickets/${masterPayerAccount.onboardingTicketId}/comments`,
            data: {
              body: comment,
              private: true,
            },
          });
        } catch (e) {
          consoleErrorWithSentry(e);
          failureMessages.push("Failed to update Zendesk ticket");
        }
      }

      if (failureMessages.length > 0) {
        throw new UpdatePendingMasterPayerAccountError(failureMessages.join(", "));
      }
    },
    [updateLogin, updateGoogleGroup, shareAWSPortfolio, api]
  );
}

async function updatePendingMasterPayerAccount(
  masterPayerAccount: MasterPayerAccountsModelSnapshot,
  mpaActivationData: MpaSubmitObject
): Promise<void> {
  try {
    await masterPayerAccount.ref.update({
      customerId: mpaActivationData.customerId,
      domain: mpaActivationData.domain,
      features: mpaActivationData.features,
      support: mpaActivationData.support,
      rootEmail: mpaActivationData.rootEmail,
      accountNumber: mpaActivationData.accountNumber,
      lastUpdated: modelServerTimestamp(),
      expectedOnboardingDate: mpaActivationData.expectedOnboardingDate ?? null,
      services: mpaActivationData.services,
      existingCustomer: mpaActivationData.existingCustomer,
      existingCustomerTier: mpaActivationData.existingCustomerTier,
      tags: mpaActivationData.tags,
    });
  } catch (exception) {
    consoleErrorWithSentry(exception);
    throw exception;
  }
}
async function updateActiveMasterPayerAccount(
  masterPayerAccount: MasterPayerAccountsModelSnapshot,
  mpaActivationData: MpaSubmitObject
): Promise<void> {
  try {
    await masterPayerAccount.ref.update({
      support: mpaActivationData.support,
      lastUpdated: modelServerTimestamp(),
      expectedOnboardingDate: mpaActivationData.expectedOnboardingDate ?? null,
    });
  } catch (exception) {
    consoleErrorWithSentry(exception);
    throw exception;
  }
}

export type EditPendingMPADialogProps = {
  masterPayerAccount: MasterPayerAccountsModelSnapshot | null;
  successMessage: string;
  onClose: () => void;
};

export const EditPendingMPADialog = ({ masterPayerAccount, successMessage, onClose }: EditPendingMPADialogProps) => {
  const updateMasterPayerAccount = useUpdatePendingMasterPayerAccount();
  return (
    <>
      {masterPayerAccount && (
        <NewMpaDialog
          masterPayerAccount={masterPayerAccount}
          open
          onCancel={onClose}
          onSubmit={async (updatedMPAData) => {
            await updateMasterPayerAccount(masterPayerAccount, updatedMPAData);
            onClose();
          }}
          successMessage={successMessage}
          failMessage="Failed to update MPA"
        />
      )}
    </>
  );
};
